"use client";

import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";

import { useClassroomStore } from "../store/useClassroomStore";
import { getRecordingManager } from "../utils/recordingUtils";

interface RecordingIndicatorProps {
  className?: string;
}

export default function RecordingIndicator({ className }: RecordingIndicatorProps) {
  const { t } = useTranslation(["classroom"]);
  const isRecording = useClassroomStore((state) => state.isRecording);
  const recordingStartTime = useClassroomStore((state) => state.recordingStartTime);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // Get recording manager to check if it's mobile recording
  const recordingManager = getRecordingManager();
  const isMobileRecording = recordingManager.isMobileRecording();

  // Update recording duration every second when recording
  useEffect(() => {
    if (!isRecording || !recordingStartTime) {
      setRecordingDuration(0);

      return;
    }

    const interval = setInterval(() => {
      const duration = Date.now() - recordingStartTime;

      setRecordingDuration(duration);
    }, 1000);

    return () => clearInterval(interval);
  }, [isRecording, recordingStartTime]);

  // Format duration as MM:SS
  const formatDuration = (durationMs: number) => {
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!isRecording) {
    return null;
  }

  // Mobile: Don't show corner indicator since we use the settings button
  if (isMobile) {
    return null;
  }

  // Desktop: Show full indicator
  return (
    <motion.div
      animate={{ opacity: 1, scale: 1 }}
      className={`bg-destructive text-destructive-foreground flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shadow-lg ${className}`}
      initial={{ opacity: 0, scale: 0.8 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        className="bg-destructive-foreground flex h-3 w-3 items-center justify-center rounded-full"
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <div className="bg-destructive h-1.5 w-1.5 rounded-full" />
      </motion.div>

      <span>
        {isMobileRecording
          ? t("recordingInProgressMobile" as any, { defaultValue: "Audio recording in progress" })
          : t("recordingInProgress" as any)}
      </span>

      <span className="font-mono text-xs">{formatDuration(recordingDuration)}</span>
    </motion.div>
  );
}
